import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, ConfirmDialog, DataGrid, Modal, StatusBadge, useToast,
  type DataGridColumn,
} from '@kss/ui';
import { Plus, RefreshCw, Trash2, Pencil, AlertTriangle } from 'lucide-react';
import { StandardAddDropdown } from '../shared/AddDropdown';
import type { Stock } from '@kss/api';
import { stockService } from '@kss/api';
import { useNameMap } from '../../hooks/useNameMaps';
import { useModulePerms } from '../../hooks/useModulePerms';
import { encodeId } from '../../utils/idCodec';

export default function Stocks() {
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const perms = useModulePerms('Stock');
  const [rows, setRows] = useState<Stock[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<(string | number)[]>([]);
  const [detailRow, setDetailRow] = useState<Stock | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Stock | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await stockService.all();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load stock';
      setError(msg); toastRef.current.error('Load failed', msg);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!confirmDelete?.stockId) return;
    setDeleting(true);
    try {
      await stockService.delete(confirmDelete.stockId);
      toast.success('Deleted', `Stock row removed`);
      setConfirmDelete(null); await load();
    } catch (e) { toast.error('Delete failed', e instanceof Error ? e.message : 'Failed'); }
    finally { setDeleting(false); }
  };

  const isLowStock = (r: Stock) => Number(r.reorderLevel ?? 0) > 0 && Number(r.quantity) <= Number(r.reorderLevel);

  // Resolve foreign-key UUIDs to human-readable names. Each map fetches
  // its `all()` once and is cached in the hook for the lifetime of the
  // page. The grid prints the name; the modal still shows the raw id
  // (alongside the name) so admins debugging by id are not blocked.
  const products = useNameMap('product');
  const stores = useNameMap('store');
  const warehouses = useNameMap('warehouse');

  const columns: DataGridColumn<Stock>[] = [
    { accessor: 'productId', title: 'Product', sortable: true, filterable: true,
      render: (r) => <span className="text-sm">{products.resolve(r.productId)}</span> },
    { accessor: 'storeId', title: 'Store', sortable: true,
      render: (r) => <span className="text-sm">{stores.resolve(r.storeId)}</span> },
    { accessor: 'warehouseId', title: 'Warehouse', sortable: true,
      render: (r) => <span className="text-sm">{warehouses.resolve(r.warehouseId)}</span> },
    { accessor: 'quantity', title: 'Quantity', sortable: true, textAlign: 'right',
      render: (r) => (
        <span className={isLowStock(r) ? 'font-semibold text-danger' : ''}>
          {isLowStock(r) && <AlertTriangle className="mr-1 inline h-3 w-3" />}
          {Number(r.quantity ?? 0).toLocaleString('en-IN')}
        </span>
      ) },
    { accessor: 'reorderLevel', title: 'Reorder', sortable: true, textAlign: 'right',
      render: (r) => Number(r.reorderLevel ?? 0).toLocaleString('en-IN') },
    { accessor: 'isActive', title: 'Status', sortable: true,
      render: (r) => <StatusBadge status={r.isActive ? 'active' : 'inactive'} /> },
    { accessor: 'stockId', title: 'Actions', width: 110,
      render: (r) => (
        <div className="flex items-center gap-1">
          {perms.canUpdate && (
            <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded text-content-secondary hover:bg-surface-card-hover"
              onClick={(e) => { e.stopPropagation(); if (r.stockId) navigate(`/panel/stock/${encodeId(r.stockId)}`); }} title="Edit">
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
          {perms.canDelete && (
            <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded text-danger hover:bg-surface-card-hover"
              onClick={(e) => { e.stopPropagation(); setConfirmDelete(r); }} title="Delete">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ) },
  ];

  return (
    <div className="-m-6 p-[3px]">
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      <DataGrid<Stock>
        title={<span className="hidden sm:inline">All stock</span>}
        columnPicker onRowClick={(row) => setDetailRow(row)} data={rows} columns={columns}
        selectable selectedIds={selected} onSelectedChange={setSelected}
        defaultPageSize={10} pageSizes={[5, 10, 25, 50, 100]}
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <StandardAddDropdown
              basePath="/panel/stock"
              newLabel="New stock entry"
              service={stockService}
              templateFields={['productId', 'storeId', 'warehouseId', 'quantity', 'reorderLevel', 'isActive']}
            />
          </div>
        }
      />
      <Modal open={!!detailRow} onClose={() => setDetailRow(null)} title="Stock details" width={520}
        footer={<>
          <Button variant="outline" onClick={() => setDetailRow(null)}>Close</Button>
          {detailRow?.stockId && (
            <Button onClick={() => { const id = detailRow.stockId!; setDetailRow(null); navigate(`/panel/stock/${encodeId(id)}`); }}>Edit</Button>
          )}
        </>}>
        {detailRow && (
          <div className="grid gap-3 rounded-md border border-border bg-surface-card-hover p-4 text-sm">
            <DetailRow label="Product" value={products.resolve(detailRow.productId)} />
            <DetailRow label="Store" value={stores.resolve(detailRow.storeId)} />
            <DetailRow label="Warehouse" value={warehouses.resolve(detailRow.warehouseId)} />
            <div className="grid grid-cols-2 gap-3">
              <DetailRow label="Quantity" value={Number(detailRow.quantity ?? 0).toLocaleString('en-IN')} />
              <DetailRow label="Reorder level" value={Number(detailRow.reorderLevel ?? 0).toLocaleString('en-IN')} />
            </div>
            <DetailRow label="Status" value={<StatusBadge status={detailRow.isActive ? 'active' : 'inactive'} />} />
            {isLowStock(detailRow) && (
              <div className="flex items-center gap-2 rounded-md border border-danger bg-danger-light p-2 text-xs text-danger">
                <AlertTriangle className="h-4 w-4" /> This item is at or below its reorder level.
              </div>
            )}
          </div>
        )}
      </Modal>
      <ConfirmDialog isOpen={!!confirmDelete} onClose={() => (deleting ? undefined : setConfirmDelete(null))}
        onConfirm={handleDelete} title="Delete stock entry"
        message={confirmDelete ? 'This will permanently remove this stock row. This action cannot be undone.' : ''}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'} variant="danger" />
    </div>
  );
}

function DetailRow({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded border border-border bg-surface-card px-3 py-2">
      <span className="text-xs text-content-muted">{label}</span>
      <span className={`text-sm font-medium text-content-primary ${mono ? 'font-mono text-xs' : ''}`}>
        {value || <em className="text-content-muted">—</em>}
      </span>
    </div>
  );
}
